import classNames from 'classnames'

import type { PortableTextComponentProps } from '~/components/atoms/BlockContent/BlockContent'
import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import Button, { ButtonVariants } from '~/components/atoms/Button/Button'
import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import { Col, Grid, Row } from '~/components/atoms/Grid'
import type { ImageProps } from '~/components/atoms/Image/Image'
import Image from '~/components/atoms/Image/Image'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { resolveLink } from '~/components/serializers'
import type { ComponentTheme } from '~/components/themes'

import styles from './TextWithImageAndStats.module.css'

export interface StatItemProps {
  number: string
  annotation: string
  _key: string
}

export interface TextWithImageAndStatsProps {
  pretitle: string
  title: string
  image: ImageProps
  dropShadowImage?: boolean
  theme?: ComponentTheme
  flip?: boolean
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  copy?: PortableTextComponentProps['blocks']
  stats: StatItemProps[]
  moduleId?: string
  testID?: string
  cardButtonFooterText?: string
  cardButtonFooterLinkText?: string
  cardButtonFooterLink?: any
  mainLink?: any
  launchForm?: string
  launchAuthForm?: string
}

export default function TextWithImageAndStats({
  pretitle,
  title,
  image,
  dropShadowImage,
  theme,
  flip,
  verticalPadding,
  noPaddingBottom,
  copy,
  stats,
  moduleId,
  cardButtonFooterText,
  cardButtonFooterLinkText,
  cardButtonFooterLink,
  testID,
  mainLink,
  launchForm,
  launchAuthForm,
}: TextWithImageAndStatsProps) {
  return (
    <div className={styles.base} data-testid={testID}>
      <Fixture
        theme={theme}
        verticalPadding={verticalPadding}
        noPaddingBottom={noPaddingBottom}
        id={moduleId}
      >
        <Container gutters={gutterTypes.always}>
          <Grid>
            <Row className={styles.alignCenter}>
              <Col
                variant="col-1/2--md"
                cssClass={classNames(
                  flip ? styles.largeGutterLeft : styles.largeGutterRight,
                  flip && styles.imageSecond
                )}
              >
                <Image
                  asset={image.asset}
                  objectFit="initial"
                  sizes="(max-width: 799px) 100vw,
                50vw"
                  alt={image.alt}
                  className={classNames(
                    styles.image,
                    dropShadowImage ? styles.dropShadow : ''
                  )}
                />
              </Col>
              <Col
                variant="col-1/2--md"
                cssClass={classNames(
                  flip ? styles.largeGutterRight : styles.largeGutterLeft
                )}
              >
                <div className={styles.text}>
                  {pretitle && (
                    <Text
                      size={TextSizes.primaryTitleMed}
                      Tag="p"
                      className={styles.pretitle}
                    >
                      <span
                        dangerouslySetInnerHTML={{
                          __html: pretitle,
                        }}
                      />
                    </Text>
                  )}
                  <Text
                    size={TextSizes.primaryTitleLrgPlus}
                    Tag="h3"
                    className={styles.title}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: title,
                      }}
                    />
                  </Text>

                  {copy && (
                    <div className={`${styles.body} prose`}>
                      <BlockContent
                        value={copy}
                        size={TextSizes.bodyLrg}
                      ></BlockContent>
                    </div>
                  )}

                  {stats && (
                    <div className={styles.stats}>
                      {stats.map(stat => {
                        return (
                          <div className={styles.stat} key={stat._key}>
                            <Text
                              Tag="h4"
                              size={TextSizes.magSecondaryTitleLrg}
                            >
                              {stat.number}
                            </Text>
                            <Text Tag="p" uppercase size={TextSizes.bodyMed}>
                              {stat.annotation}
                            </Text>
                          </div>
                        )
                      })}
                    </div>
                  )}
                  {cardButtonFooterText ? (
                    <div className={styles.contentFooter}>
                      {cardButtonFooterText && (
                        <Text
                          size={TextSizes.bodyMed}
                          className={styles.cardButtonFooterText}
                        >
                          {cardButtonFooterText}
                        </Text>
                      )}

                      {cardButtonFooterLinkText && (
                        <div className={styles.cardButtonFooterLink}>
                          <Button
                            variant={ButtonVariants.textLink}
                            launchAuthForm={launchAuthForm}
                            launchForm={launchForm}
                            link={resolveLink(mainLink)}
                            target={mainLink.target}
                          >
                            {cardButtonFooterLinkText}
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </Col>
            </Row>
          </Grid>
        </Container>
      </Fixture>
    </div>
  )
}
